import React, { useState } from 'react';

function formatPrice(p) {
  const v = parseFloat(p) || 0;
  return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
}

function RebookSection({ booking, instances, formatDate, onRebook }) {
  const [targetId, setTargetId] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [priceChoice, setPriceChoice] = useState(null); // { currentPrice, targetPrice, targetCourseName }
  const [fullWarning, setFullWarning] = useState(null); // { confirmedCount, maxParticipants, ... }

  const today = React.useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);
  // Exkludera nuvarande kurs, behåll bara framtida instanser (kommer redan sorterade från parent)
  const candidates = (instances || []).filter(i =>
    i.id !== booking.courseInstanceId &&
    i.startDate && new Date(i.startDate) >= today
  );

  const submit = async (extras = {}) => {
    setError('');
    setLoading(true);
    try {
      const result = await onRebook({
        targetInstanceId: parseInt(targetId),
        reason: reason || undefined,
        ...extras
      });
      if (result?.handled) return; // parent stängde modal eller hanterade
    } catch (err) {
      const resp = err.response?.data;
      if (resp?.error === 'price_differs') {
        setPriceChoice(resp.data);
      } else if (resp?.error === 'target_full') {
        setFullWarning(resp.data);
      } else {
        setError(resp?.error || 'Kunde inte omboka');
      }
    } finally {
      setLoading(false);
    }
  };

  if (booking.status !== 'Confirmed') return null;
  if (candidates.length === 0) {
    return (
      <>
        <h3 className="form-section-title">Omboka till annan kurs</h3>
        <p style={{ color: '#666', fontStyle: 'italic' }}>Inga framtida kurser tillgängliga att omboka till.</p>
      </>
    );
  }

  return (
    <>
      <h3 className="form-section-title">Omboka till annan kurs</h3>
      <div className="form-group">
        <label>Ny kurs</label>
        <select value={targetId} onChange={(e) => { setTargetId(e.target.value); setPriceChoice(null); setFullWarning(null); setError(''); }}>
          <option value="">-- Välj ny kurs --</option>
          {candidates.map(i => (
            <option key={i.id} value={i.id}>
              {i.course?.name} - {formatDate(i.startDate)} ({i.location})
            </option>
          ))}
        </select>
      </div>
      <div className="form-group">
        <label>Anledning (frivilligt — loggas i bokningsnoteringar)</label>
        <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="T.ex. 'Deltagaren kunde inte närvara den ursprungliga dagen'" />
      </div>

      {error && (
        <div style={{ background: '#fee', color: '#c00', padding: '10px 12px', borderRadius: '4px', marginBottom: '10px' }}>
          {error}
        </div>
      )}

      {priceChoice && (
        <div style={{ background: '#fff3cd', borderLeft: '4px solid #ffc107', padding: '15px 18px', borderRadius: '4px', marginBottom: '12px' }}>
          <div style={{ marginBottom: '10px' }}>
            <strong>Priserna skiljer sig.</strong> Nuvarande pris: <strong>{formatPrice(priceChoice.currentPrice)}</strong>,
            mål-kursens pris: <strong>{formatPrice(priceChoice.targetPrice)}</strong>.
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-primary" disabled={loading}
              onClick={() => submit({ newPrice: priceChoice.currentPrice })}>
              Behåll {formatPrice(priceChoice.currentPrice)}
            </button>
            <button type="button" className="btn btn-secondary" disabled={loading}
              onClick={() => submit({ newPrice: priceChoice.targetPrice })}>
              Uppdatera till {formatPrice(priceChoice.targetPrice)}
            </button>
            <button type="button" className="btn btn-secondary" disabled={loading}
              onClick={() => setPriceChoice(null)}>
              Avbryt
            </button>
          </div>
        </div>
      )}

      {fullWarning && (
        <div style={{ background: '#fee', borderLeft: '4px solid #e74c3c', padding: '15px 18px', borderRadius: '4px', marginBottom: '12px' }}>
          <div style={{ marginBottom: '10px' }}>
            <strong>Mål-kursen är fullbokad.</strong> {fullWarning.confirmedCount}/{fullWarning.maxParticipants} bekräftade bokningar.
            Vill du omboka ändå?
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button type="button" className="btn btn-warning" disabled={loading}
              onClick={() => submit({ confirmFull: true })}>
              Omboka ändå
            </button>
            <button type="button" className="btn btn-secondary" disabled={loading}
              onClick={() => setFullWarning(null)}>
              Avbryt
            </button>
          </div>
        </div>
      )}

      {!priceChoice && !fullWarning && (
        <div style={{ marginBottom: '20px' }}>
          <button type="button" className="btn btn-primary" disabled={!targetId || loading}
            onClick={() => submit()}>
            {loading ? 'Ombokar...' : 'Omboka deltagare'}
          </button>
          <span style={{ marginLeft: '12px', color: '#666', fontSize: '0.85rem' }}>
            Bekräftelse-mejl skickas automatiskt till deltagaren.
          </span>
        </div>
      )}
    </>
  );
}

function EditBookingModal({ show, booking, form, setForm, formatDate, onSubmit, onClose, instances, onRebook }) {
  if (!show || !booking) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <h2>Redigera bokning</h2>
        <p className="modal-subtitle">
          {booking.courseInstance?.course?.name} - {formatDate(booking.courseInstance?.startDate)}
        </p>

        <form onSubmit={onSubmit}>
          <h3 className="form-section-title">Deltagarinformation</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Förnamn</label>
              <input type="text" value={form.participantFirstName}
                onChange={(e) => setForm({...form, participantFirstName: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Efternamn</label>
              <input type="text" value={form.participantLastName}
                onChange={(e) => setForm({...form, participantLastName: e.target.value})} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>E-post</label>
              <input type="email" value={form.participantEmail}
                onChange={(e) => setForm({...form, participantEmail: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Telefon</label>
              <input type="tel" value={form.participantPhone}
                onChange={(e) => setForm({...form, participantPhone: e.target.value})} />
            </div>
          </div>
          <div className="form-group">
            <label>Specialkost / Allergier</label>
            <input type="text" value={form.specialDiet}
              onChange={(e) => setForm({...form, specialDiet: e.target.value})}
              placeholder="T.ex. vegetarisk, glutenfri..." />
          </div>

          <h3 className="form-section-title">Företags- och fakturainformation</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Företag</label>
              <input type="text" value={form.company}
                onChange={(e) => setForm({...form, company: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Organisationsnummer</label>
              <input type="text" value={form.organizationNumber}
                onChange={(e) => setForm({...form, organizationNumber: e.target.value})}
                placeholder="XXXXXX-XXXX" />
            </div>
          </div>
          <div className="form-group">
            <label>Fakturaadress</label>
            <textarea value={form.billingAddress}
              onChange={(e) => setForm({...form, billingAddress: e.target.value})}
              rows="2" placeholder="Gatuadress, postnummer, ort..." />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Fakturareferens</label>
              <input type="text" value={form.invoiceReference}
                onChange={(e) => setForm({...form, invoiceReference: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Kostnadsställe</label>
              <input type="text" value={form.costCenter}
                onChange={(e) => setForm({...form, costCenter: e.target.value})} />
            </div>
          </div>

          <h3 className="form-section-title">Boknings- och fakturastatus</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Pris (kr)</label>
              <input type="number" value={form.price || ''}
                onChange={(e) => setForm({...form, price: e.target.value})}
                placeholder="0" min="0" step="1" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Bokningsstatus</label>
              <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})}>
                <option value="Confirmed">Bekräftad</option>
                <option value="Waitlist">Väntelista</option>
                <option value="Cancelled">Avbokad</option>
              </select>
            </div>
            <div className="form-group">
              <label>Fakturastatus</label>
              <select value={form.invoiceStatus} onChange={(e) => setForm({...form, invoiceStatus: e.target.value})}>
                <option value="NotInvoiced">Ej fakturerad</option>
                <option value="Invoiced">Fakturerad</option>
                <option value="Paid">Betald</option>
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Fakturanummer</label>
              <input type="text" value={form.invoiceNumber}
                onChange={(e) => setForm({...form, invoiceNumber: e.target.value})}
                placeholder="T.ex. 12345" />
            </div>
            <div className="form-group">
              <label>Faktureringsdatum</label>
              <input type="date" value={form.invoiceDate}
                onChange={(e) => setForm({...form, invoiceDate: e.target.value})} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Närvaro</label>
              <select value={form.attended} onChange={(e) => setForm({...form, attended: e.target.value})}>
                <option value="">Ej registrerad</option>
                <option value="true">Närvarande</option>
                <option value="false">Frånvarande</option>
              </select>
            </div>
            <div className="form-group">
              <label>Avbokningsorsak</label>
              <input type="text" value={form.cancellationReason}
                onChange={(e) => setForm({...form, cancellationReason: e.target.value})}
                placeholder="Om avbokad..." />
            </div>
          </div>

          <h3 className="form-section-title">Interna anteckningar</h3>
          <div className="form-group">
            <textarea value={form.notes}
              onChange={(e) => setForm({...form, notes: e.target.value})}
              rows="3" placeholder="Interna anteckningar om bokningen..." />
          </div>

          {instances && onRebook && (
            <RebookSection
              booking={booking}
              instances={instances}
              formatDate={formatDate}
              onRebook={onRebook}
            />
          )}

          <div className="form-actions">
            <button type="submit" className="btn btn-success">Spara ändringar</button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Avbryt</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditBookingModal;
